/**
 * Avisa quando há uma versão nova do ERP publicada (igual ao Cut).
 *
 * Verifica o `asset-manifest.json` a cada 5 minutos e sempre que a aba volta
 * ao foco (o caso real: "abri o ERP depois do deploy"). Só roda no build de
 * produção — no dev server não há bundle com hash para comparar.
 *
 * Quem dispensa o aviso escolhe a hora: ele some para ESTA versão e volta se
 * sair outra. Atualizar = recarregar; o index.html é sem cache e o service
 * worker busca a navegação na rede primeiro, então a recarga traz o novo.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { bundleCarregado, bundleDoManifesto, haVersaoNova } from "../utils/versaoDoApp";

export const INTERVALO_DE_VERIFICACAO_MS = 5 * 60 * 1000;
const MANIFESTO = "/asset-manifest.json";

async function lerVersaoPublicada() {
  // `t=` fura qualquer cache intermediário; o arquivo já sai "no-store".
  const resposta = await fetch(`${MANIFESTO}?t=${Date.now()}`, { cache: "no-store" });
  if (!resposta.ok) return null;
  return bundleDoManifesto(await resposta.json());
}

export default function useVersaoNova({ ativo = process.env.NODE_ENV === "production" } = {}) {
  const [publicada, setPublicada] = useState(null);
  const [dispensada, setDispensada] = useState(null);
  const carregada = useRef(null);

  const verificar = useCallback(async () => {
    try {
      const versao = await lerVersaoPublicada();
      if (versao) setPublicada(versao);
    } catch (erro) {
      // Sem rede ou deploy no meio do caminho: tenta de novo no próximo ciclo.
      console.warn("[PharuERP] Não foi possível verificar se há versão nova:", erro);
    }
  }, []);

  useEffect(() => {
    if (!ativo) return undefined;
    carregada.current = bundleCarregado();
    if (!carregada.current) return undefined;
    const intervalo = window.setInterval(verificar, INTERVALO_DE_VERIFICACAO_MS);
    const aoVoltar = () => {
      if (document.visibilityState === "visible") verificar();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    window.addEventListener("focus", aoVoltar);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener("visibilitychange", aoVoltar);
      window.removeEventListener("focus", aoVoltar);
    };
  }, [ativo, verificar]);

  const disponivel = haVersaoNova(carregada.current, publicada) && publicada !== dispensada;
  const dispensar = useCallback(() => setDispensada(publicada), [publicada]);
  const atualizar = useCallback(() => window.location.reload(), []);

  return { disponivel, dispensar, atualizar, verificar };
}
